import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'
import { RootState } from '../../../../../../store/reducers/rootReducer'
import { IClientsItem } from '../../../../../modules/API/Defaults/models/customers'
import { IQuotationTitle, IQuotationDetails } from '../types/quotation'
import { Orders } from '../../../../../modules/API/Orders/orders'
import { LINKS } from '../../../../../Routs/config'
import { handleNotifications } from '../../../../../generic/utils/handleNotifications'
import useDebounce from '../../seller/Helpers/useDebounce'
import { MOCK_DETAILS, MOCK_TITLES } from '../constance/mock'

interface PendingEdit {
  titleID: number
  detail: IQuotationDetails
}

const useOrderQuotationDetails = () => {
  const { id } = useParams() as { id: string }
  const navigate = useNavigate()

  const customerList: IClientsItem[] = useSelector(
    (state: RootState) => state.defaults.clientList.list
  )

  const isCreateMode = id === 'new'

  const [titleData, setTitleData] = useState<IQuotationTitle | null>(null)
  const [details, setDetails] = useState<IQuotationDetails[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isInitModalOpen, setIsInitModalOpen] = useState(isCreateMode)

  const [customerID, setCustomerID] = useState<number>(0)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  // Debounce mechanism for price edits
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null)
  const debouncedEdit = useDebounce(pendingEdit, 500)
  const lastSentRef = useRef<string | null>(null)

  // View flow: fetch existing quotation by title ID
  useEffect(() => {
    if (isCreateMode) return

    const titleID = Number(id)
    if (isNaN(titleID)) return

    const fetchDetails = async () => {
      setIsLoading(true)
      try {
        const response = await Orders.getQuotationDetailsByTitleID(titleID)
        setTitleData(response.title)
        setDetails(response.details)
        // setTitleData(MOCK_TITLES[0])
        // setDetails(MOCK_DETAILS)
      } catch {
        handleNotifications('שגיאה בטעינת פרטי הצעה', { type: 'error' })
      } finally {
        setIsLoading(false)
      }
    }

    fetchDetails()
  }, [id, isCreateMode])

  // Send debounced price edit to server
  useEffect(() => {
    if (!debouncedEdit) return

    const editKey = `${debouncedEdit.detail.recId}-${debouncedEdit.detail.price}`
    if (lastSentRef.current === editKey) return
    lastSentRef.current = editKey

    const sendEdit = async () => {
      try {
        await Orders.editQuotation(debouncedEdit.detail)
      } catch {
        handleNotifications('שגיאה בעדכון מחיר', { type: 'error' })
      }
    }

    sendEdit()
  }, [debouncedEdit])

  // Create flow: user confirms customer + date
  const handleConfirmCreate = useCallback(async () => {
    if (customerID === 0) return

    setIsLoading(true)
    try {
      const formattedDate = format(selectedDate, 'yyyyMMdd')
      const productList = await Orders.getQotationProductListByCustomerID(customerID, formattedDate)
      const response = await Orders.createQuotation({
        customerId: customerID,
        date: formattedDate,
        details: productList.products,
      })
      setTitleData(response.title)
      setDetails(response.details)
      setIsInitModalOpen(false)

      navigate(`${LINKS.orders.quotation.details}/${response.title.recId}`, { replace: true })
      handleNotifications('הצעת מחיר נוצרה בהצלחה', { type: 'success' })
    } catch {
      handleNotifications('שגיאה ביצירת הצעת מחיר', { type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }, [customerID, selectedDate, history])

  const handlePriceChange = useCallback(
    (recId: number, newPrice: number) => {
      setDetails((prev) => {
        const updated = prev.map((d) =>
          d.recId === recId ? { ...d, price: newPrice, override: true } : d
        )

        const changedDetail = updated.find((d) => d.recId === recId)
        if (changedDetail && titleData) {
          setPendingEdit({ titleID: titleData.recId, detail: changedDetail })
        }

        return updated
      })
    },
    [titleData]
  )

  const handleChangeStatus = useCallback(
    async (titleID: number, status: number) => {
      if (!titleData) return

      const prevTitle = { ...titleData }
      setTitleData({ ...titleData, status })

      try {
        await Orders.changeStatusQuotation(titleID, status)
        handleNotifications('הסטטוס עודכן', { type: 'success' })
      } catch {
        setTitleData(prevTitle)
        handleNotifications('שגיאה בעדכון סטטוס', { type: 'error' })
      }
    },
    [titleData]
  )

  const handleGoBack = useCallback(() => {
    navigate(LINKS.orders.quotation.mainUrl)
  }, [history])

  return {
    isCreateMode,
    isInitModalOpen,
    isLoading,
    titleData,
    details,
    customerList,
    customerID,
    setCustomerID,
    selectedDate,
    setSelectedDate,
    handleConfirmCreate,
    handlePriceChange,
    handleChangeStatus,
    handleGoBack,
  }
}

export default useOrderQuotationDetails
